import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface PendingUpload {
  tempId: string;
  fileName: string;
  fileSize: number;
  progress: number;
  status: 'uploading' | 'attached' | 'failed';
  mediaId?: string;
  error?: string;
  // Auto-retry attempts the orchestrator has burned for this upload's attach
  // since the last user reset. Bounded by MAX_ATTACH_RETRIES per cycle.
  attachRetryCount?: number;
  // Total number of times the user has clicked Retry for this upload. Bounded
  // by MAX_USER_RETRIES so a permanently-failing attach can't be hammered
  // indefinitely (each click otherwise unlocks another full auto-retry ladder).
  userRetryCount?: number;
  // True after the orchestrator has surfaced the attach-exhausted toast for
  // this upload. Cleared on resetUploadForRetry so a re-exhaustion after a
  // user retry surfaces the toast again instead of being silently swallowed.
  exhaustedNotified?: boolean;
  // Timestamp (ms) of the last user-triggered retry reset. The orchestrator
  // honours a short grace window after this point: a stale "failed" UploadItem
  // observed via MediaUploadContext during the IndexedDB-read + setUploadItems
  // gap would otherwise re-mark this pendingUpload as failed before
  // resumeUploadWithFile has had a chance to publish the fresh upload.
  retryStartedAt?: number;
  // True for S3-multipart pendingUploads whose media row is already attached to
  // the request at creation time (via media.footage on the store payload). The
  // mediaId is pinned up-front so use-upload.tsx can find this entry on
  // completeMultipartUpload, but the actual S3 object isn't durable until those
  // chunks finish uploading. The orchestrator must NOT treat a pinned mediaId
  // as "ready to attach" for these — markUploadAttached is driven by use-upload
  // when S3 truly completes. Without this flag, the orchestrator's retry-after-
  // tus-success branch fires on the first poll and finalises the request to
  // Queued while the file is still uploading. (Karina #2372 follow-up.)
  preAttached?: boolean;
}

const MAX_USER_RETRIES = 3;
// Grace period after a user-triggered reset during which the orchestrator
// ignores stale "failed" UploadItem observations. Sized to comfortably cover
// the IndexedDB read (typically 10–50 ms) plus a React render plus one full
// orchestrator poll tick (POLL_INTERVAL_MS = 2 s).
const RETRY_RESET_GRACE_MS = 5000;

export interface BackgroundUploadRequest {
  requestId: string;
  requestTitle: string;
  pendingUploads: PendingUpload[];
  createdAt: number;
}

// Shared with request.helper.ts's isRequestLocked so "is this request
// actively uploading" has exactly one definition (#2567).
export const hasUploadingPendingUpload = (request?: BackgroundUploadRequest): boolean =>
  !!request?.pendingUploads.some((upload) => upload.status === 'uploading');

interface BackgroundUploadStore {
  activeRequests: Record<string, BackgroundUploadRequest>;
  /**
   * Map of "<fileName>|<fileSize>" -> mediaId, filled by MediaUploadContext the
   * moment a tus upload's media object is known. The orchestrator reads this
   * as a deterministic alternative to racing uploadItems before the context
   * cleans them up.
   */
  completedMediaIds: Record<string, string>;
  addRequest: (
    requestId: string,
    requestTitle: string,
    uploads: PendingUpload[],
  ) => void;
  updateUploadProgress: (requestId: string, tempId: string, progress: number) => void;
  markUploadAttached: (requestId: string, tempId: string, mediaId: string) => void;
  markUploadFailed: (requestId: string, tempId: string, error?: string, incrementAttachRetry?: boolean) => void;
  setPendingUploadMediaId: (requestId: string, tempId: string, mediaId: string) => void;
  markExhaustedNotified: (requestId: string, tempId: string) => void;
  /**
   * Reset a failed upload so the orchestrator picks it up again.
   * Returns true if the reset was applied; false if the per-upload user-retry
   * cap (MAX_USER_RETRIES) is already reached — caller should surface a
   * "retry limit reached" message instead of silently doing nothing.
   */
  resetUploadForRetry: (requestId: string, tempId: string) => boolean;
  removeRequest: (requestId: string) => void;
  recordCompletedUpload: (fileName: string, fileSize: number, mediaId: string) => void;
  clearCompletedMediaId: (fileName: string, fileSize: number) => void;
  getOverallProgress: (requestId: string) => number;
  isRequestUploading: (requestId: string) => boolean;
  getAllUploadingRequestIds: () => string[];
}

export { MAX_USER_RETRIES };

const isUserRetryAllowed = (upload: PendingUpload): boolean =>
  (upload.userRetryCount ?? 0) < MAX_USER_RETRIES;

// A rehydrated PendingUpload from the persisted store can carry a
// retryStartedAt that's hours old (TTL is 24 h). That's intentionally fine:
// `now - retryStartedAt` will exceed RETRY_RESET_GRACE_MS and grace will
// correctly NOT apply — the orchestrator gets the next legitimate failure
// signal on the first poll after rehydrate.
const isWithinRetryGrace = (upload: PendingUpload, now = Date.now()): boolean =>
  upload.retryStartedAt !== undefined && now - upload.retryStartedAt < RETRY_RESET_GRACE_MS;

export { isUserRetryAllowed, isWithinRetryGrace };

export const useBackgroundUploadStore = create<BackgroundUploadStore>()(
  persist(
    (set, get) => ({
      activeRequests: {},
      completedMediaIds: {},

      addRequest: (requestId, requestTitle, uploads) =>
        set((state) => {
          // Clear any stale completedMediaIds for these uploads so the
          // orchestrator can't attach a previously-seen mediaId before the
          // NEW tus upload has finished and reported its own mediaId.
          const freshCompletedMediaIds = { ...state.completedMediaIds };
          for (const upload of uploads) {
            delete freshCompletedMediaIds[`${upload.fileName}|${upload.fileSize}`];
          }
          return {
            activeRequests: {
              ...state.activeRequests,
              [requestId]: {
                requestId,
                requestTitle,
                pendingUploads: uploads,
                createdAt: Date.now(),
              },
            },
            completedMediaIds: freshCompletedMediaIds,
          };
        }),

      updateUploadProgress: (requestId, tempId, progress) =>
        set((state) => {
          const request = state.activeRequests[requestId];
          if (!request) return state;
          return {
            activeRequests: {
              ...state.activeRequests,
              [requestId]: {
                ...request,
                pendingUploads: request.pendingUploads.map((u) =>
                  u.tempId === tempId ? { ...u, progress } : u,
                ),
              },
            },
          };
        }),

      markUploadAttached: (requestId, tempId, mediaId) =>
        set((state) => {
          const request = state.activeRequests[requestId];
          if (!request) return state;
          return {
            activeRequests: {
              ...state.activeRequests,
              [requestId]: {
                ...request,
                pendingUploads: request.pendingUploads.map((u) =>
                  u.tempId === tempId
                    ? { ...u, status: 'attached' as const, progress: 100, mediaId }
                    : u,
                ),
              },
            },
          };
        }),

      markUploadFailed: (requestId, tempId, error, incrementAttachRetry) =>
        set((state) => {
          const request = state.activeRequests[requestId];
          if (!request) return state;
          return {
            activeRequests: {
              ...state.activeRequests,
              [requestId]: {
                ...request,
                pendingUploads: request.pendingUploads.map((u) =>
                  u.tempId === tempId
                    ? {
                        ...u,
                        status: 'failed' as const,
                        error,
                        ...(incrementAttachRetry
                          ? { attachRetryCount: (u.attachRetryCount ?? 0) + 1 }
                          : {}),
                      }
                    : u,
                ),
              },
            },
          };
        }),

      setPendingUploadMediaId: (requestId, tempId, mediaId) =>
        set((state) => {
          const request = state.activeRequests[requestId];
          if (!request) return state;
          return {
            activeRequests: {
              ...state.activeRequests,
              [requestId]: {
                ...request,
                pendingUploads: request.pendingUploads.map((u) =>
                  u.tempId === tempId && u.mediaId !== mediaId ? { ...u, mediaId } : u,
                ),
              },
            },
          };
        }),

      markExhaustedNotified: (requestId, tempId) =>
        set((state) => {
          const request = state.activeRequests[requestId];
          if (!request) return state;
          return {
            activeRequests: {
              ...state.activeRequests,
              [requestId]: {
                ...request,
                pendingUploads: request.pendingUploads.map((u) =>
                  u.tempId === tempId && !u.exhaustedNotified
                    ? { ...u, exhaustedNotified: true }
                    : u,
                ),
              },
            },
          };
        }),

      resetUploadForRetry: (requestId, tempId) => {
        let applied = false;
        set((state) => {
          const request = state.activeRequests[requestId];
          if (!request) return state;
          const target = request.pendingUploads.find((u) => u.tempId === tempId);
          if (!target) return state;
          // Cap user-triggered retries. Each click otherwise re-arms the
          // orchestrator's auto-retry ladder (3 attach attempts), so without
          // a cap a permanent failure can be hammered indefinitely.
          if ((target.userRetryCount ?? 0) >= MAX_USER_RETRIES) return state;
          applied = true;
          return {
            activeRequests: {
              ...state.activeRequests,
              [requestId]: {
                ...request,
                pendingUploads: request.pendingUploads.map((u) =>
                  u.tempId === tempId
                    ? {
                        ...u,
                        status: 'uploading' as const,
                        progress: 0,
                        error: undefined,
                        // mediaId preserved: if tus already succeeded, the
                        // orchestrator's main-loop branch re-attaches with
                        // this mediaId directly (avoids the duplicate-filename
                        // race in the completedMediaIds fallback cache).
                        attachRetryCount: 0,
                        // Re-arm the exhaustion toast so a second exhaustion
                        // after this retry surfaces feedback to the user
                        // instead of being suppressed by a stale flag.
                        exhaustedNotified: false,
                        userRetryCount: (u.userRetryCount ?? 0) + 1,
                        // Stamp the reset so the orchestrator's poll can
                        // distinguish a fresh retry from the stale 'failed'
                        // UploadItem still sitting in MediaUploadContext until
                        // resumeUploadWithFile publishes the replacement.
                        retryStartedAt: Date.now(),
                      }
                    : u,
                ),
              },
            },
          };
        });
        return applied;
      },

      removeRequest: (requestId) =>
        set((state) => {
          const { [requestId]: _, ...rest } = state.activeRequests;
          return { activeRequests: rest };
        }),

      recordCompletedUpload: (fileName, fileSize, mediaId) =>
        set((state) => ({
          completedMediaIds: {
            ...state.completedMediaIds,
            [`${fileName}|${fileSize}`]: mediaId,
          },
        })),

      clearCompletedMediaId: (fileName, fileSize) =>
        set((state) => {
          const key = `${fileName}|${fileSize}`;
          if (!(key in state.completedMediaIds)) return state;
          const { [key]: _, ...rest } = state.completedMediaIds;
          return { completedMediaIds: rest };
        }),

      getOverallProgress: (requestId) => {
        const request = get().activeRequests[requestId];
        if (!request || request.pendingUploads.length === 0) return 0;
        const totalProgress = request.pendingUploads.reduce((sum, u) => sum + u.progress, 0);
        return Math.round(totalProgress / request.pendingUploads.length);
      },

      isRequestUploading: (requestId) => hasUploadingPendingUpload(get().activeRequests[requestId]),

      getAllUploadingRequestIds: () => {
        return Object.keys(get().activeRequests).filter((id) => get().isRequestUploading(id));
      },
    }),
    {
      name: 'beCreatives_background_uploads',
      storage: createJSONStorage(() => localStorage),
      // Only persist activeRequests — completedMediaIds is transient per-session
      // state and persisting it caused stale mediaIds from prior uploads of the
      // same filename to be attached to new requests.
      partialize: (state) => ({ activeRequests: state.activeRequests }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const TTL_MS = 24 * 60 * 60 * 1000;
        const now = Date.now();
        const cleaned: Record<string, BackgroundUploadRequest> = {};
        for (const [id, request] of Object.entries(state.activeRequests)) {
          // Evict entries older than 24 hours
          if (now - request.createdAt >= TTL_MS) continue;
          // Migrate removed statuses from previous version back to uploading
          cleaned[id] = {
            ...request,
            pendingUploads: request.pendingUploads.map((u) => {
              const status = u.status as string;
              if (status === 'completed' || status === 'attaching') {
                return { ...u, status: 'uploading' as const };
              }
              return u;
            }),
          };
        }
        state.activeRequests = cleaned;
      },
    },
  ),
);
